// ─── Storage versioning ───────────────────────────────────────────────────────
// Bump STORAGE_VERSION to force a full clear on next deploy when needed.
const STORAGE_VERSION = '1';
const VERSION_KEY = 'bao_storage_version';

// ─── Reload-loop protection ───────────────────────────────────────────────────
// Used by StorageErrorBoundary to cap the number of auto-reloads per session.
export const RELOAD_COUNT_KEY = 'bao_storage_reload_count';

// ─── Internal helpers ─────────────────────────────────────────────────────────

function _safeJsonGet<T>(storage: Storage, key: string, defaultValue: T): T {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw) as T;
  } catch (e) {
    console.error(`[BAO Storage] Corrupt value for key "${key}", removing`, e);
    try {
      storage.removeItem(key);
    } catch {
      // ignore
    }
    return defaultValue;
  }
}

function _safeJsonSet(storage: Storage, key: string, value: unknown): boolean {
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    console.error(`[BAO Storage] Failed to set key "${key}"`, e);
    return false;
  }
}

function _safeRawGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch (e) {
    console.error(`[BAO Storage] Failed to get key "${key}"`, e);
    return null;
  }
}

function _safeRawSet(storage: Storage, key: string, value: string): boolean {
  try {
    storage.setItem(key, value);
    return true;
  } catch (e) {
    console.error(`[BAO Storage] Failed to set key "${key}"`, e);
    return false;
  }
}

function _safeRemove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

// ─── localStorage — JSON values ───────────────────────────────────────────────

export const safeGetItem = <T>(key: string, defaultValue: T): T => _safeJsonGet(localStorage, key, defaultValue);

export const safeSetItem = (key: string, value: unknown): boolean => _safeJsonSet(localStorage, key, value);

export const safeRemoveItem = (key: string): void => _safeRemove(localStorage, key);

// ─── sessionStorage — raw string values (table filters/sort) ─────────────────

export const safeSessionGetRaw = (key: string): string | null => _safeRawGet(sessionStorage, key);

export const safeSessionSetRaw = (key: string, value: string): boolean => _safeRawSet(sessionStorage, key, value);

export const safeSessionRemoveRaw = (key: string): void => _safeRemove(sessionStorage, key);

/**
 * Returns all current sessionStorage keys matching a predicate.
 * Iterating and removing in the same loop corrupts indices — always collect first.
 */
export function getSessionKeys(predicate?: (key: string) => boolean): string[] {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k !== null && (!predicate || predicate(k))) keys.push(k);
    }
    return keys;
  } catch {
    return [];
  }
}

// ─── Regex helper ─────────────────────────────────────────────────────────────

/** Escapes a string for safe use inside `new RegExp(...)`. */
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ─── Nuclear option ───────────────────────────────────────────────────────────

export function clearCorruptedStorage(): void {
  // Preserve the reload-loop counter so StorageErrorBoundary can still count
  // attempts after the reload that follows this call.
  let reloadCount: string | null = null;
  try {
    reloadCount = sessionStorage.getItem(RELOAD_COUNT_KEY);
  } catch {
    // ignore
  }

  try {
    localStorage.clear();
  } catch {
    // ignore
  }
  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }

  if (reloadCount !== null) {
    try {
      sessionStorage.setItem(RELOAD_COUNT_KEY, reloadCount);
    } catch {
      // ignore
    }
  }

  console.warn('[BAO Storage] All storage cleared');
}

// ─── Versioning ───────────────────────────────────────────────────────────────

export function checkAndInitStorageVersion(): void {
  try {
    const stored = localStorage.getItem(VERSION_KEY);
    if (stored === null) {
      localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
    } else if (stored !== STORAGE_VERSION) {
      console.warn(`[BAO Storage] Version mismatch (stored: ${stored}, expected: ${STORAGE_VERSION}) — clearing`);
      clearCorruptedStorage();
      localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
    }
    // App started successfully — reset the reload-loop counter.
    sessionStorage.removeItem(RELOAD_COUNT_KEY);
  } catch (e) {
    console.error('[BAO Storage] Version check failed', e);
  }
}

// ─── Startup diagnostic log ───────────────────────────────────────────────────

export function logStorageStartup(): void {
  try {
    const _version = localStorage.getItem(VERSION_KEY) ?? '(none)';

    const localKeys = Object.keys(localStorage);
    const sessionKeys = Object.keys(sessionStorage);

    let _localBytes = 0;
    for (const k of localKeys) _localBytes += k.length + (localStorage.getItem(k)?.length ?? 0);
    let _sessionBytes = 0;
    for (const k of sessionKeys) _sessionBytes += k.length + (sessionStorage.getItem(k)?.length ?? 0);
  } catch (e) {
    console.warn('[BAO Storage] Startup log failed', e);
  }
}
